import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { timelineApi } from "../../lib/api";
import KIcon from "./KIcon";
import ProjectPicker from "./ProjectPicker";
import { SummaryTiles } from "./PaymentBits";
import { TaskRow, taskStatus, projectKind } from "./CabinetDashboard";
import { useClientProject, resolveScope } from "./projectScope";
import { cn } from "../../lib/utils";
import { tr } from "./i18n";

const FILTERS = [
  { key: "all", label: "Усі" },
  { key: "wait", label: "Чекають вас" },
  { key: "work", label: "В роботі" },
  { key: "plan", label: "Заплановано" },
  { key: "done", label: "Готово" },
];
const ORDER = { wait: 0, work: 1, plan: 2, done: 3 };

/**
 * «Задачі» — its own section (not an anchor on the overview): every task of
 * the selected project (or all projects), filtered by status, in the same
 * row style as the overview; a click opens the task.
 */
const ClientTasksView = () => {
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [timelines, setTimelines] = useState({});
  const [scope, setScope] = useClientProject();
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/client/portal");
        setPortal(res.data);
        const all = (res.data.projects || []).flatMap((p) => p.tasks || []);
        const map = {};
        await Promise.all(all.map(async (t) => {
          try { const r = await timelineApi.getTaskTimeline(t.id); if (r.data) map[t.id] = r.data; } catch (_) { /* no timeline */ }
        }));
        setTimelines(map);
      } catch (_) {
        toast.error(tr("Не вдалося завантажити задачі"));
      }
    })();
  }, []);

  if (!portal) {
    return <main className="main"><p style={{ color: "var(--muted)" }}>{tr("Завантаження…")}</p></main>;
  }
  const projects = portal.projects || [];
  const sel = resolveScope(scope, projects);
  const inScope = sel === "all" ? projects : projects.filter((p) => p.id === sel);
  const allTasks = inScope.flatMap((p) => p.tasks || []);
  const count = (k) => (k === "all" ? allTasks.length : allTasks.filter((t) => taskStatus(t) === k).length);
  const ALL = { id: "all", name: tr("Усі проєкти"), status: null };

  const sections = inScope
    .map((p) => ({
      project: p,
      tasks: (p.tasks || [])
        .filter((t) => filter === "all" || taskStatus(t) === filter)
        .sort((a, b) => ORDER[taskStatus(a)] - ORDER[taskStatus(b)] || (a.deadline || "").localeCompare(b.deadline || "")),
    }))
    .filter((s) => s.tasks.length)
    // projects with something waiting on the client first, then active, then done
    .sort((a, b) => Math.min(...a.tasks.map((t) => ORDER[taskStatus(t)])) - Math.min(...b.tasks.map((t) => ORDER[taskStatus(t)])));

  return (
    <main className="main">
      <header className="top">
        <div>
          <p className="eyebrow">{tr("Кабінет клієнта")}</p>
          <h1 className="hello">{tr("Задачі")}</h1>
        </div>
        {projects.length > 1 && (
          <ProjectPicker
            projects={[ALL, ...projects]}
            selected={sel === "all" ? ALL : projects.find((p) => p.id === sel)}
            onSelect={setScope}
            flagOf={(pr) => (pr.id !== "all" && (pr.tasks || []).some((t) => t.needs_approval) ? "bg-amber-500" : null)}
          />
        )}
      </header>

      <SummaryTiles
        tiles={[
          { label: tr("Усього задач"), value: String(count("all")) },
          { label: tr("Чекають вашої відповіді"), value: String(count("wait")), tone: count("wait") ? "due" : "muted" },
          { label: tr("В роботі"), value: String(count("work")) },
          { label: tr("Готово"), value: String(count("done")), tone: count("done") ? "paid" : "muted" },
        ]}
      />

      <div className="flex flex-wrap gap-1 rounded-xl border border-zinc-800 bg-zinc-900/60 p-1 w-fit">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
              filter === f.key ? "bg-zinc-800 text-zinc-50 font-semibold" : "text-zinc-400 hover:text-zinc-100")}>
            {tr(f.label)}<span className="text-[11px] text-zinc-500">{count(f.key)}</span>
          </button>
        ))}
      </div>

      {sections.length === 0 ? (
        <section className="card"><p style={{ color: "var(--muted)" }}>{tr("Задач з таким статусом немає.")}</p></section>
      ) : sections.map(({ project, tasks }) => (
        <section key={project.id} className="card tasks" style={{ borderTop: "1px solid var(--line)", paddingTop: 20 }}>
          <div className="sh">
            <KIcon name="tasks" />
            <h3>{project.name}</h3>
            <span className="cnt">{tr(projectKind(project))} · {tasks.length} {tr(tasks.length === 1 ? "задача" : "задач")}</span>
          </div>
          <ol className="tlist">
            {tasks.map((t) => (
              <TaskRow key={t.id} t={t} timeline={timelines[t.id]} pmName={project.project_manager?.name}
                onOpen={() => navigate(`/client/tasks/${t.id}`)} />
            ))}
          </ol>
        </section>
      ))}
    </main>
  );
};

export default ClientTasksView;
